import React, { useState, useLayoutEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from '@/components/ui/carousel';
import { PayPalPlan } from '../types';
import { useSubscriptionData } from '../hooks/useSubscriptionData';
import { useSubscriptionActions } from '../hooks/useSubscriptionActions';
import SubscriptionsShell from './SubscriptionsShell';
import PlanCard from './PlanCard';
import { usePermissions, type Feature } from '../../../contexts/PermissionContext';
import { getTierFeatures } from '../../../utils/tierFeatures';
import type { PermissionDisplayMetadata } from '../../../services/permissionService';

export default function SubscriptionsDesktop() {
  const data = useSubscriptionData();
  const actions = useSubscriptionActions(data.hasPendingDowngrade, data.subscriptionStatus, data.fetchSubscriptionData);
  const { getPermissionMetadata } = usePermissions();

  // Local state for carousel APIs (needed for mobile view within desktop component)
  const [monthlyCarouselApi, setMonthlyCarouselApi] = useState<any>(null);
  const [annualCarouselApi, setAnnualCarouselApi] = useState<any>(null);

  // Update carousel position only if tier changes after initial render
  // (Initial position is set via startIndex in opts to avoid visual jump)
  const prevTierRef = useRef<string | null>(null);
  
  useLayoutEffect(() => {
    if (monthlyCarouselApi && data.activeTier && prevTierRef.current !== null && prevTierRef.current !== data.activeTier) {
      monthlyCarouselApi.scrollTo(data.getInitialCarouselIndex, true);
    }
    prevTierRef.current = data.activeTier || null;
  }, [monthlyCarouselApi, data.getInitialCarouselIndex, data.activeTier]);

  useLayoutEffect(() => {
    if (annualCarouselApi && data.activeTier && prevTierRef.current !== null && prevTierRef.current !== data.activeTier) {
      annualCarouselApi.scrollTo(data.getInitialCarouselIndex, true);
    }
  }, [annualCarouselApi, data.getInitialCarouselIndex, data.activeTier]);

  const renderMonthlyPlans = ({
    activeTier,
    activeBillingCycle,
    hasPaidSubscription,
    hasRoleBasedAccess,
    hasPendingDowngrade,
    subscriptionStatus,
    unifiedPlans,
    tierLimits,
    paypalSdkLoaded,
    upgradeLoading,
    downgradeLoading,
    onUpgrade,
    onDowngrade,
    fetchSubscriptionData,
    hasPendingCancellationToFree,
    isFamilyMember,
  }: any) => {
    // Render both mobile carousel and desktop grid (like original)
    return (
      <>
        {/* Mobile Carousel */}
        <div className="block md:hidden max-w-5xl mx-auto">
          <Carousel
            opts={{
              align: "start",
              loop: true,
            }}
            setApi={setMonthlyCarouselApi}
            className="w-full"
          >
            <CarouselContent className="-ml-2 md:-ml-4">
              {/* Free Tier - Mobile */}
              {(() => {
                const freeLimits = tierLimits.get('free');
                const isActive = activeTier === 'free';
                
                return (
                  <CarouselItem key="free" className="pl-3 pr-1 pt-2 pb-2 md:pl-4 md:pt-0 md:pb-0">
                    <Card className={`relative flex flex-col ${
                      isActive ? 'ring-2 ring-purple-500' : ''
                    } ${
                      hasPendingCancellationToFree ? 'ring-2 ring-red-500 border-red-500' : ''
                    } subscription-tier-card w-full`}>
                      {isActive && (
                        <Badge className="absolute top-2 right-2" variant="default">
                          Current
                        </Badge>
                      )}
                      {hasPendingCancellationToFree && (() => {
                        const effectiveDate = subscriptionStatus?.next_billing_date || subscriptionStatus?.subscription_end_date;
                        return (
                          <Badge className="absolute top-2 right-2 bg-red-500 text-white" variant="default">
                            {effectiveDate
                              ? `Starting ${new Date(effectiveDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                              : 'Starting Next Billing Cycle'}
                          </Badge>
                        );
                      })()}
                      <CardHeader>
                        <div className="flex items-center gap-2">
                          <CardTitle className="text-xl">Free</CardTitle>
                        </div>
                        <CardDescription>
                          Try out and save time!
                          {hasPendingCancellationToFree && (() => {
                            const effectiveDate = subscriptionStatus?.next_billing_date || subscriptionStatus?.subscription_end_date;
                            return effectiveDate ? (
                              <span className="block mt-1 text-red-600 dark:text-red-400">
                                Changes take effect on {new Date(effectiveDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                              </span>
                            ) : null;
                          })()}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="flex flex-col flex-grow overflow-visible">
                        <div className="mb-4">
                          <span className="text-3xl font-bold">$0</span>
                          <span className="text-muted-foreground"> per month</span>
                        </div>
                        <ul className="space-y-2 mb-4 text-sm flex-grow">
                          {getTierFeatures({
                            tier: 'free',
                            limits: freeLimits || null,
                            getPermissionMetadata: getPermissionMetadata as (feature: Feature) => PermissionDisplayMetadata | undefined,
                          }).map((feature, index) => (
                            <li key={index} className="flex items-center gap-2">
                              {!feature.isHeader && (
                                feature.included ? (
                                  <span className="text-green-500">✓</span>
                                ) : (
                                  <span className="text-red-500">✗</span>
                                )
                              )}
                              {feature.isHeader && <span className="flex-shrink-0" />}
                              <span className={feature.bold ? 'font-medium' : feature.included ? '' : 'text-muted-foreground'}>
                                {feature.text}
                              </span>
                            </li>
                          ))}
                        </ul>
                        <div className="mt-auto w-full" style={{ height: '120px', minHeight: '120px' }}></div>
                        <Button className="w-full" variant={isActive ? 'default' : 'outline'} disabled={true}>
                          {isActive ? 'Current Plan' : 'Not Available'}
                        </Button>
                      </CardContent>
                    </Card>
                  </CarouselItem>
                );
              })()}

              {/* Dynamic monthly tiers - Mobile */}
              {unifiedPlans.flatMap((unifiedPlan: any) => {
                const plans = Object.values(unifiedPlan.monthly_plans || {});
                return plans;
              })
              .filter((plan: PayPalPlan) => plan.tier !== 'free')
              .sort((a: PayPalPlan, b: PayPalPlan) => {
                const tierOrder: Record<string, number> = { starter: 1, pro: 2 };
                return (tierOrder[a.tier] || 999) - (tierOrder[b.tier] || 999);
              })
              .map((plan: PayPalPlan) => {
                const limits = tierLimits.get(plan.tier);
                const containerId = `mobile-paypal-button-container-${plan.paypal_plan_id}`;
                
                return (
                  <CarouselItem key={plan.id} className="pl-3 pr-1 pt-2 pb-2 md:pl-4 md:pt-0 md:pb-0">
                    <PlanCard
                      plan={plan}
                      limits={limits}
                      subscriptionStatus={subscriptionStatus}
                      activeTier={activeTier}
                      activeBillingCycle={activeBillingCycle}
                      hasPaidSubscription={hasPaidSubscription}
                      hasRoleBasedAccess={hasRoleBasedAccess}
                      hasPendingDowngrade={hasPendingDowngrade}
                      paypalSdkLoaded={paypalSdkLoaded}
                      upgradeLoading={upgradeLoading}
                      downgradeLoading={downgradeLoading}
                      freeLimits={tierLimits.get('free')}
                      starterLimits={tierLimits.get('starter')}
                      isFamilyMemberNotOwner={isFamilyMember}
                      containerId={containerId}
                      onUpgrade={onUpgrade}
                      onDowngrade={onDowngrade}
                      onSubscriptionSuccess={fetchSubscriptionData}
                    />
                  </CarouselItem>
                );
              })}
            </CarouselContent>
            <CarouselPrevious className="left-2" />
            <CarouselNext className="right-2" />
          </Carousel>
        </div>

        {/* Desktop Carousel - Monthly Plans */}
        <div className="hidden md:block max-w-5xl mx-auto px-4">
          <Carousel
            opts={{
              align: "start",
              loop: true,
              startIndex: 1,  // Start at Starter (skip Free)
            }}
            className="w-full"
          >
            <CarouselContent className="ml-0">
              {/* Free Tier - Always shown first */}
              <CarouselItem className="pl-2 pr-2 basis-1/3 flex py-2">
                {(() => {
                  const freeLimits = tierLimits.get('free');
                  const isActive = activeTier === 'free';

                  return (
                    <Card className={`relative flex flex-col h-full ${
                      isActive ? 'ring-2 ring-purple-500' : ''
                    } ${
                      hasPendingCancellationToFree ? 'ring-2 ring-red-500 border-red-500' : ''
                    } subscription-tier-card w-full`}>
                      {isActive && (
                        <Badge className="absolute top-2 right-2" variant="default">
                          Current
                        </Badge>
                      )}
                      {hasPendingCancellationToFree && (
                        <Badge className="absolute top-2 right-2 bg-red-500 text-white" variant="default">
                          {subscriptionStatus?.next_billing_date
                            ? `Starting ${new Date(subscriptionStatus.next_billing_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                            : 'Starting Next Billing Cycle'}
                        </Badge>
                      )}
                      <CardHeader>
                        <div className="flex items-center gap-2">
                          <CardTitle className="text-xl">Free</CardTitle>
                        </div>
                        <CardDescription>Try out and save time!</CardDescription>
                      </CardHeader>
                      <CardContent className="flex flex-col flex-grow overflow-visible">
                        <div className="mb-4">
                          <span className="text-3xl font-bold">$0</span>
                          <span className="text-muted-foreground"> per month</span>
                        </div>
                        <ul className="space-y-2 mb-4 text-sm flex-grow">
                          {getTierFeatures({
                            tier: 'free',
                            limits: freeLimits || null,
                            getPermissionMetadata: getPermissionMetadata as (feature: Feature) => PermissionDisplayMetadata | undefined,
                          }).map((feature, index) => (
                            <li key={index} className="flex items-center gap-2">
                              {!feature.isHeader && (
                                feature.included ? (
                                  <span className="text-green-500">✓</span>
                                ) : (
                                  <span className="text-red-500">✗</span>
                                )
                              )}
                              {feature.isHeader && <span className="flex-shrink-0" />}
                              <span className={feature.bold ? 'font-medium' : feature.included ? '' : 'text-muted-foreground'}>
                                {feature.text}
                              </span>
                            </li>
                          ))}
                        </ul>
                        <div className="mt-auto w-full" style={{ height: '120px', minHeight: '120px' }}></div>
                        <Button className="w-full" variant={isActive ? 'default' : 'outline'} disabled={true}>
                          {isActive ? 'Current Plan' : 'Not Available'}
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })()}
              </CarouselItem>

              {/* Dynamic monthly tiers from unified app plans */}
              {unifiedPlans.flatMap((unifiedPlan: any) => {
                const plans = Object.values(unifiedPlan.monthly_plans || {});
                return plans;
              })
              .filter((plan: PayPalPlan) => plan.tier !== 'free')
              .sort((a: PayPalPlan, b: PayPalPlan) => {
                const tierOrder: Record<string, number> = { starter: 1, pro: 2, family: 3 };
                return (tierOrder[a.tier] || 999) - (tierOrder[b.tier] || 999);
              })
              .map((plan: PayPalPlan) => {
                const limits = tierLimits.get(plan.tier);
                const containerId = `desktop-paypal-button-container-${plan.paypal_plan_id}`;

                return (
                  <CarouselItem key={`desktop-${plan.id}`} className="pl-2 pr-2 basis-1/3 flex py-2">
                    <PlanCard
                      plan={plan}
                      limits={limits}
                      subscriptionStatus={subscriptionStatus}
                      activeTier={activeTier}
                      activeBillingCycle={activeBillingCycle}
                      hasPaidSubscription={hasPaidSubscription}
                      hasRoleBasedAccess={hasRoleBasedAccess}
                      hasPendingDowngrade={hasPendingDowngrade}
                      paypalSdkLoaded={paypalSdkLoaded}
                      upgradeLoading={upgradeLoading}
                      downgradeLoading={downgradeLoading}
                      containerId={containerId}
                      freeLimits={tierLimits.get('free')}
                      starterLimits={tierLimits.get('starter')}
                      isFamilyMemberNotOwner={isFamilyMember}
                      onUpgrade={onUpgrade}
                      onDowngrade={onDowngrade}
                      onSubscriptionSuccess={fetchSubscriptionData}
                    />
                  </CarouselItem>
                );
              })}
            </CarouselContent>
            <CarouselPrevious className="left-2" />
            <CarouselNext className="right-2" />
          </Carousel>
        </div>
      </>
    );
  };

  const renderAnnualPlans = ({
    activeTier,
    activeBillingCycle,
    hasPaidSubscription,
    hasRoleBasedAccess,
    hasPendingDowngrade,
    subscriptionStatus,
    unifiedPlans,
    tierLimits,
    paypalSdkLoaded,
    upgradeLoading,
    downgradeLoading,
    onUpgrade,
    onDowngrade,
    fetchSubscriptionData,
    hasPendingCancellationToFree,
    isFamilyMember,
  }: any) => {
    // Render both mobile carousel and desktop grid (like original)
    const annualPlans = unifiedPlans.flatMap((unifiedPlan: any) => Object.values(unifiedPlan.annual_plans || {}));
    const seen = new Set<string>();
    const dedup: PayPalPlan[] = [];
    annualPlans.forEach((plan: PayPalPlan) => {
      if (!seen.has(plan.tier)) {
        seen.add(plan.tier);
        dedup.push(plan);
      }
    });

    return (
      <>
        {/* Mobile Carousel - Annual */}
        <div className="block md:hidden max-w-5xl mx-auto">
          <Carousel
            opts={{
              align: "start",
              loop: true,
              startIndex: data.getInitialCarouselIndex,
            }}
            setApi={setAnnualCarouselApi}
            className="w-full"
          >
            <CarouselContent className="-ml-2 md:-ml-4">
              {/* Free Tier - Mobile Annual */}
              {(() => {
                const freeLimits = tierLimits.get('free');
                const isActive = activeTier === 'free';

                return (
                  <CarouselItem key="free-annual-mobile" className="pl-3 pr-1 pt-2 pb-2 md:pl-4 md:pt-0 md:pb-0">
                    <Card className={`relative flex flex-col ${
                      isActive ? 'ring-2 ring-purple-500' : ''
                    } ${
                      hasPendingCancellationToFree ? 'ring-2 ring-red-500 border-red-500' : ''
                    } subscription-tier-card w-full`}>
                      {isActive && (
                        <Badge className="absolute top-2 right-2" variant="default">
                          Current
                        </Badge>
                      )}
                      {hasPendingCancellationToFree && (() => {
                        const effectiveDate = subscriptionStatus?.next_billing_date || subscriptionStatus?.subscription_end_date;
                        return (
                          <Badge className="absolute top-2 right-2 bg-red-500 text-white" variant="default">
                            {effectiveDate
                              ? `Starting ${new Date(effectiveDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                              : 'Starting Next Billing Cycle'}
                          </Badge>
                        );
                      })()}
                      <CardHeader>
                        <div className="flex items-center gap-2">
                          <CardTitle className="text-xl">Free</CardTitle>
                        </div>
                        <CardDescription>Try out and save time!</CardDescription>
                      </CardHeader>
                      <CardContent className="flex flex-col flex-grow overflow-visible">
                        <div className="mb-4">
                          <span className="text-3xl font-bold">$0</span>
                          <span className="text-muted-foreground"> per year</span>
                        </div>
                        <ul className="space-y-2 mb-4 text-sm flex-grow">
                          {getTierFeatures({
                            tier: 'free',
                            limits: freeLimits || null,
                            getPermissionMetadata: getPermissionMetadata as (feature: Feature) => PermissionDisplayMetadata | undefined,
                          }).map((feature, index) => (
                            <li key={index} className="flex items-center gap-2">
                              {!feature.isHeader && (
                                feature.included ? (
                                  <span className="text-green-500">✓</span>
                                ) : (
                                  <span className="text-red-500">✗</span>
                                )
                              )}
                              {feature.isHeader && <span className="flex-shrink-0" />}
                              <span className={feature.bold ? 'font-medium' : feature.included ? '' : 'text-muted-foreground'}>
                                {feature.text}
                              </span>
                            </li>
                          ))}
                        </ul>
                        <div className="mt-auto w-full" style={{ height: '120px', minHeight: '120px' }}></div>
                        <Button className="w-full" variant={isActive ? 'default' : 'outline'} disabled={true}>
                          {isActive ? 'Current Plan' : 'Not Available'}
                        </Button>
                      </CardContent>
                    </Card>
                  </CarouselItem>
                );
              })()}

              {dedup
                .filter(plan => plan.tier !== 'free')
                .sort((a: PayPalPlan, b: PayPalPlan) => {
                  // Fixed order: Starter, Pro (Free is already shown first)
                  const tierOrder: Record<string, number> = { starter: 1, pro: 2 };
                  return (tierOrder[a.tier] || 999) - (tierOrder[b.tier] || 999);
                })
                .map((plan: PayPalPlan) => {
                  const limits = tierLimits.get(plan.tier);
                  const monthlyPlan = unifiedPlans
                    .flatMap((up: any) => Object.values(up.monthly_plans || {}))
                    .find((p: PayPalPlan) => p.tier === plan.tier);
                  const containerId = `mobile-paypal-button-container-${plan.paypal_plan_id}`;
                  
                  return (
                    <CarouselItem key={plan.id} className="pl-3 pr-1 pt-2 pb-2 md:pl-4 md:pt-0 md:pb-0">
                      <PlanCard
                        plan={plan}
                        limits={limits}
                        subscriptionStatus={subscriptionStatus}
                        activeTier={activeTier}
                        activeBillingCycle={activeBillingCycle}
                        hasPaidSubscription={hasPaidSubscription}
                        hasRoleBasedAccess={hasRoleBasedAccess}
                        hasPendingDowngrade={hasPendingDowngrade}
                        paypalSdkLoaded={paypalSdkLoaded}
                        upgradeLoading={upgradeLoading}
                        downgradeLoading={downgradeLoading}
                        containerId={containerId}
                        monthlyPlan={monthlyPlan}
                        freeLimits={tierLimits.get('free')}
                        starterLimits={tierLimits.get('starter')}
                        isFamilyMemberNotOwner={isFamilyMember}
                        onUpgrade={onUpgrade}
                        onDowngrade={onDowngrade}
                        onSubscriptionSuccess={fetchSubscriptionData}
                      />
                    </CarouselItem>
                  );
                })}
            </CarouselContent>
            <CarouselPrevious className="left-2" />
            <CarouselNext className="right-2" />
          </Carousel>
        </div>

        {/* Desktop Carousel - Annual Plans */}
        <div className="hidden md:block max-w-5xl mx-auto px-4">
          <Carousel
            opts={{
              align: "start",
              loop: true,
              startIndex: 1,  // Start at Starter (skip Free)
            }}
            className="w-full"
          >
            <CarouselContent className="ml-0">
              {/* Free Tier - Desktop Annual */}
              <CarouselItem className="pl-2 pr-2 basis-1/3 flex py-2">
                {(() => {
                  const freeLimits = tierLimits.get('free');
                  const isActive = activeTier === 'free';

                  return (
                    <Card className={`relative flex flex-col h-full ${
                      isActive ? 'ring-2 ring-purple-500' : ''
                    } ${
                      hasPendingCancellationToFree ? 'ring-2 ring-red-500 border-red-500' : ''
                    } subscription-tier-card w-full`}>
                      {isActive && (
                        <Badge className="absolute top-2 right-2" variant="default">
                          Current
                        </Badge>
                      )}
                      {hasPendingCancellationToFree && (
                        <Badge className="absolute top-2 right-2 bg-red-500 text-white" variant="default">
                          {subscriptionStatus?.next_billing_date
                            ? `Starting ${new Date(subscriptionStatus.next_billing_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                            : 'Starting Next Billing Cycle'}
                        </Badge>
                      )}
                      <CardHeader>
                        <div className="flex items-center gap-2">
                          <CardTitle className="text-xl">Free</CardTitle>
                        </div>
                        <CardDescription>Try out and save time!</CardDescription>
                      </CardHeader>
                      <CardContent className="flex flex-col flex-grow overflow-visible">
                        <div className="mb-4">
                          <span className="text-3xl font-bold">$0</span>
                          <span className="text-muted-foreground"> per year</span>
                        </div>
                        <ul className="space-y-2 mb-4 text-sm flex-grow">
                          {getTierFeatures({
                            tier: 'free',
                            limits: freeLimits || null,
                            getPermissionMetadata: getPermissionMetadata as (feature: Feature) => PermissionDisplayMetadata | undefined,
                          }).map((feature, index) => (
                            <li key={index} className="flex items-center gap-2">
                              {!feature.isHeader && (
                                feature.included ? (
                                  <span className="text-green-500">✓</span>
                                ) : (
                                  <span className="text-red-500">✗</span>
                                )
                              )}
                              {feature.isHeader && <span className="flex-shrink-0" />}
                              <span className={feature.bold ? 'font-medium' : feature.included ? '' : 'text-muted-foreground'}>
                                {feature.text}
                              </span>
                            </li>
                          ))}
                        </ul>
                        <div className="mt-auto w-full" style={{ height: '120px', minHeight: '120px' }}></div>
                        <Button className="w-full" variant={isActive ? 'default' : 'outline'} disabled={true}>
                          {isActive ? 'Current Plan' : 'Not Available'}
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })()}
              </CarouselItem>

              {dedup
                .filter(plan => plan.tier !== 'free')
                .sort((a: PayPalPlan, b: PayPalPlan) => {
                  // Fixed order: Starter, Pro, Family (Free is already shown first)
                  const tierOrder: Record<string, number> = { starter: 1, pro: 2, family: 3 };
                  return (tierOrder[a.tier] || 999) - (tierOrder[b.tier] || 999);
                })
              .map((plan: PayPalPlan) => {
                const limits = tierLimits.get(plan.tier);
                const monthlyPlan = unifiedPlans
                  .flatMap((up: any) => Object.values(up.monthly_plans || {}))
                  .find((p: PayPalPlan) => p.tier === plan.tier);
                const containerId = `desktop-paypal-button-container-${plan.paypal_plan_id}`;

                return (
                  <CarouselItem key={`desktop-annual-${plan.id}`} className="pl-2 pr-2 basis-1/3 flex py-2">
                    <PlanCard
                      plan={plan}
                      limits={limits}
                      subscriptionStatus={subscriptionStatus}
                      activeTier={activeTier}
                      activeBillingCycle={activeBillingCycle}
                      hasPaidSubscription={hasPaidSubscription}
                      hasRoleBasedAccess={hasRoleBasedAccess}
                      hasPendingDowngrade={hasPendingDowngrade}
                      paypalSdkLoaded={paypalSdkLoaded}
                      upgradeLoading={upgradeLoading}
                      downgradeLoading={downgradeLoading}
                      containerId={containerId}
                      monthlyPlan={monthlyPlan}
                      freeLimits={tierLimits.get('free')}
                      starterLimits={tierLimits.get('starter')}
                      isFamilyMemberNotOwner={isFamilyMember}
                      onUpgrade={onUpgrade}
                      onDowngrade={onDowngrade}
                      onSubscriptionSuccess={fetchSubscriptionData}
                    />
                  </CarouselItem>
                );
              })}
            </CarouselContent>
            <CarouselPrevious className="left-2" />
            <CarouselNext className="right-2" />
          </Carousel>
        </div>
      </>
    );
  };

  return (
    <SubscriptionsShell
      data={data}
      actions={actions}
      renderMonthlyPlans={renderMonthlyPlans}
      renderAnnualPlans={renderAnnualPlans}
    />
  );
}
